// SPDX-FileCopyrightText: 2026 Ben Richardson
// SPDX-License-Identifier: AGPL-3.0-or-later
//
// Colour is defined ONCE and referenced everywhere. An award system is the same
// colour in the timeline, the hierarchy, the matrix and the legend; if it were
// not, a reader would have to relearn the encoding on every tab.

/** The seven award systems, in register order. */
export const SYSTEM_COLOUR: Record<string, string> = {
  'Order of Australia': '#8a6a1f',
  Imperial: '#7a3b56',
  Realm: '#1f3a5f',
  Meritorious: '#2f6b62',
  Bravery: '#a8442a',
  Defence: '#5b5f7a',
  'Unit Citation': '#7d7a4e',
  Unknown: '#9aa1ae',
};

export const systemColour = (s: string): string => SYSTEM_COLOUR[s] ?? '#9aa1ae';

/** Order of Australia levels, highest first, on a single-hue ramp. */
export const LEVEL_COLOUR: Record<string, string> = {
  AC: '#5c4310',
  AO: '#8a6a1f',
  AM: '#b08f3d',
  OAM: '#d8bd79',
  AK: '#3f2d0a',
  AD: '#3f2d0a',
};

export const levelColour = (c: string): string => LEVEL_COLOUR[c] ?? '#c9bfa4';

/** Sex encoding. `unknown` is a real category and is drawn, never hidden. */
export const SEX_COLOUR = {
  female: '#7a3b56',
  male: '#1f3a5f',
  unknown: '#b9b3a4',
} as const;

/** Twenty service domains. Distinguishable, and stable across views. */
export const DOMAIN_COLOUR: Record<string, string> = {
  health: '#2f6b62',
  community: '#8a6a1f',
  education: '#1f3a5f',
  veterans: '#7a3b56',
  arts: '#a8442a',
  sport: '#3f7a3a',
  business: '#5b5f7a',
  welfare: '#b3701a',
  environment: '#417a5a',
  law: '#43506b',
  government: '#6b5a86',
  science: '#2a6b8a',
  faith: '#8a5a2f',
  primary: '#6b7a2f',
  emergency: '#b3261e',
  media: '#7a5f8a',
  youth: '#2f8a7a',
  indigenous: '#8a4a1f',
  multicultural: '#5a7a8a',
  women: '#93436b',
  other: '#9aa1ae',
};

export const domainColour = (d: string): string => DOMAIN_COLOUR[d] ?? '#9aa1ae';

/**
 * Sequential ramp for choropleths and matrices, light to dark on one hue.
 * Deliberately NOT a quantile scale: honours-per-capita is heavily skewed, and
 * a quantile scale would paint the flat bottom eight deciles as if they differed
 * as much as the break-away top. Callers pass explicit breaks.
 */
export const RAMP = ['#f6efdd', '#e8d7ae', '#d3ba7c', '#b99a50', '#96762f', '#6d5316', '#463205'];

export function rampColour(value: number, breaks: number[]): string {
  if (!Number.isFinite(value)) return '#eceae4';
  let i = 0;
  while (i < breaks.length && value >= breaks[i]) i++;
  return RAMP[Math.min(i, RAMP.length - 1)];
}

/** Diverging ramp for observed-minus-expected matrices. */
export const DIVERGING = {
  low: '#3a6b8a',
  mid: '#f2efe8',
  high: '#a8442a',
};

export function divergingColour(z: number, cap = 3): string {
  const t = Math.max(-1, Math.min(1, z / cap));
  const mix = (a: string, b: string, k: number): string => {
    const p = (h: string): number[] => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
    const [r1, g1, b1] = p(a);
    const [r2, g2, b2] = p(b);
    const c = (x: number, y: number): string =>
      Math.round(x + (y - x) * k)
        .toString(16)
        .padStart(2, '0');
    return `#${c(r1, r2)}${c(g1, g2)}${c(b1, b2)}`;
  };
  return t < 0 ? mix(DIVERGING.mid, DIVERGING.low, -t) : mix(DIVERGING.mid, DIVERGING.high, t);
}
